import { createSelector } from 'reselect';
import { List, Map } from 'immutable';

import { nullIfFirstArgNegative, nullIfArgByIndexesNegative, fromOwnProp } from '@Utils';

import { databases } from '@Services/UserDatabases/UserDatabasesSelectors';

import { alertsReducerKey } from './AlertsConstants';

export function alerts(state) {
  return state.get(alertsReducerKey);
}

export function alertsIds(state) {
  return alerts(state).get('ids');
}

export function alertsItems (state) {
  return alerts(state).get('items');
}

export const alertById = createSelector(
  [alertsItems, fromOwnProp('id')],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItems, id) => {
    return alertsItems.get(id);
  })
);

export const alertDatabaseId = createSelector(
  [alertById],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertById) => {
    return alertById.get('databaseId');
  })
);

export const alertDbName = createSelector(
  [alertDatabaseId, databases],
  nullIfArgByIndexesNegative(
    // eslint-disable-next-line no-shadow
    (alertDatabaseId, databases) => {
      return databases.getIn([alertDatabaseId, 'name']);
    },
    [0, 1]
  )
);

export const alertsItem = createSelector(
  [alertById],
  nullIfFirstArgNegative((alert) => {
    return alert.get('alertsItem');
  })
);

export const alertContext = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    return alertsItem.get('context');
  })
);

export const alertLastSaved = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    return alertsItem.get('lastSaved');
  })
);

export const alertUuid = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    return alertsItem.get('alertUuid');
  })
);

export const alertName = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    return alertsItem.get('alertName');
  })
);

export const alertDescription = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    return alertsItem.get('description');
  })
);

export const alertStructures = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    const facts = alertsItem.getIn(['queryFactItem', 'facts'], List());

    return facts.reduce((res, fact) => {
      const structures = fact.get('structures', List());

      return res.concat(structures);
    }, List());
  })
);

export const alertResults = createSelector(
  [alertsItem],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItem) => {
    return alertsItem.get('results');
  })
);

// returns Map
export const alertsGroupsMap = createSelector(
  [alertsIds, alertsItems],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsIds, alertsItems) => {
    return alertsIds.reduce((GroupsMap, id) => {
      const groupId = alertsItems.getIn([id, 'alertsItem', 'groupId']);
      const GroupList = GroupsMap.get(groupId, List());

      return GroupsMap.set(groupId, GroupList.push(id))
    }, Map());
  })
);

// returns List
export const groupsIds = createSelector(
  [alertsGroupsMap],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsGroupsMap) => {
    return alertsGroupsMap.keySeq().toList();
  })
);

// returns List
export const alertsIdsByGroupId = createSelector(
  [alertsGroupsMap, fromOwnProp('groupId')],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsGroupsMap, groupId) => {
    return alertsGroupsMap.get(groupId);
  })
);

// returns List
export const allDatabasesByGroupId = createSelector(
  [alertsItems, alertsGroupsMap, fromOwnProp('groupId')],
  // eslint-disable-next-line no-shadow
  nullIfFirstArgNegative((alertsItems, alertsGroupsMap, groupId) => {
    const GroupList = alertsGroupsMap.get(groupId);

    return GroupList && GroupList.map((id) => alertsItems.getIn([id, 'databaseId']));
  })
);

export function isMounted(state) {
  return alerts(state).get('mounted');
}

export function refetch(state) {
  return alerts(state).get('refetch');
}
